/**
 * QUIPWA — replicated tables of `_state.db`
 *
 * One declarative spec per table: its columns (in a fixed order, used for both
 * SELECT and comparison), how to compute a row's key, and which tombstone
 * entity deletes it. Export, merge and apply all read from these specs, so a
 * new column only has to be added here (and in a migration).
 */

import type { CardState, SessionMode } from '../../types/domain.ts'

/**
 * What a tombstone deletes. deck_file is keyed by a .wflash Drive file id: it
 * tells deck discovery not to re-import the file of a deck the user deleted.
 */
export type TombstoneEntity = 'deck_instance' | 'deck_file' | 'tag' | 'deck_tag' | 'review_state' | 'session' | 'socratic_item'

export interface Tombstone {
  entity: TombstoneEntity
  key: string
  /** HLC timestamp of the deletion. */
  deleted_at: string
}

export interface DeckInstanceRow {
  id: string
  deck_id: string
  deck_name: string
  wflash_drive_path: string
  drive_file_id: string
  drive_modified_at: number
  imported_at: number
  card_count: number
  card_style: string | null
  deck_color: string | null
  updated_at: string
}

export interface TagRow {
  id: string
  name: string
  parent_id: string | null
  color: string
  created_at: number
  updated_at: string
}

export interface DeckTagRow {
  deck_instance_id: string
  tag_id: string
  updated_at: string
}

export interface SessionRow {
  id: string
  started_at: number
  ended_at: number | null
  mode: SessionMode
  cards_total: number
  cards_reviewed: number
  avg_time_ms: number | null
  cal_event_id: string | null
  updated_at: string
}

export interface SessionDeckRow {
  session_id: string
  deck_instance_id: string
  cards_from_deck: number
  updated_at: string
}

export interface ReviewStateRow {
  card_id: string
  deck_instance_id: string
  due: number
  stability: number
  difficulty: number
  elapsed_days: number
  scheduled_days: number
  reps: number
  lapses: number
  state: CardState
  last_review: number | null
  avg_time_ms: number | null
  updated_at: string
}

export interface ReviewLogRow {
  card_id: string
  deck_instance_id: string
  session_id: string
  reviewed_at: number
  rating: 1 | 2 | 3 | 4 | 5
  time_ms: number
  stability_before: number
  stability_after: number
  difficulty_before: number
  difficulty_after: number
  scheduled_days_before: number
  scheduled_days_after: number
}

/** A replicated user setting (JSON value), last-writer-wins per key. */
export interface SettingRow {
  key: string
  value: string
  updated_at: string
}

/** One item of the Socratic exam bank. `guide` is the ItemGuide JSON. */
export interface SocraticItemRow {
  id: string
  source_note_id: string | null
  concept_id: string
  concept_name: string
  prompt: string
  guide: string
  status: string
  created_at: number
  updated_at: string
}

/** One presentation of an item (lib/socratic Attempt as JSON). Never changes. */
export interface SocraticAttemptRow {
  id: string
  session_id: string
  started_at: number
  concept_id: string
  data: string
}

/** A dispute, override or resolution on an attempt. Never changes. */
export interface SocraticEventRow {
  id: string
  attempt_id: string
  kind: string
  at: string
  payload: string
}

/** Everything in `_state.db` that replicates, as plain rows. */
export interface ReplicatedState {
  settings: SettingRow[]
  deckInstances: DeckInstanceRow[]
  tags: TagRow[]
  deckTags: DeckTagRow[]
  sessions: SessionRow[]
  sessionDecks: SessionDeckRow[]
  reviewStates: ReviewStateRow[]
  reviewLogs: ReviewLogRow[]
  socraticItems: SocraticItemRow[]
  socraticAttempts: SocraticAttemptRow[]
  socraticEvents: SocraticEventRow[]
  tombstones: Tombstone[]
}

export interface TableSpec<Row> {
  table: string
  columns: readonly (keyof Row & string)[]
  keyColumns: readonly (keyof Row & string)[]
  key: (row: Row) => string
}

function spec<Row>(table: string, columns: readonly (keyof Row & string)[], keyColumns: readonly (keyof Row & string)[]): TableSpec<Row> {
  return {
    table,
    columns,
    keyColumns,
    key: (row) => keyColumns.map((c) => String(row[c])).join('|'),
  }
}

export const DECK_INSTANCES = spec<DeckInstanceRow>(
  'deck_instances',
  ['id', 'deck_id', 'deck_name', 'wflash_drive_path', 'drive_file_id', 'drive_modified_at', 'imported_at', 'card_count', 'card_style', 'deck_color', 'updated_at'],
  ['id']
)

export const TAGS = spec<TagRow>('tags', ['id', 'name', 'parent_id', 'color', 'created_at', 'updated_at'], ['id'])

export const DECK_TAGS = spec<DeckTagRow>('deck_tags', ['deck_instance_id', 'tag_id', 'updated_at'], ['deck_instance_id', 'tag_id'])

export const SESSIONS = spec<SessionRow>(
  'sessions',
  ['id', 'started_at', 'ended_at', 'mode', 'cards_total', 'cards_reviewed', 'avg_time_ms', 'cal_event_id', 'updated_at'],
  ['id']
)

export const SESSION_DECKS = spec<SessionDeckRow>('session_decks', ['session_id', 'deck_instance_id', 'cards_from_deck', 'updated_at'], ['session_id', 'deck_instance_id'])

export const REVIEW_STATES = spec<ReviewStateRow>(
  'review_state',
  ['card_id', 'deck_instance_id', 'due', 'stability', 'difficulty', 'elapsed_days', 'scheduled_days', 'reps', 'lapses', 'state', 'last_review', 'avg_time_ms', 'updated_at'],
  ['card_id', 'deck_instance_id']
)

export const REVIEW_LOGS = spec<ReviewLogRow>(
  'review_log',
  ['card_id', 'deck_instance_id', 'session_id', 'reviewed_at', 'rating', 'time_ms', 'stability_before', 'stability_after', 'difficulty_before', 'difficulty_after', 'scheduled_days_before', 'scheduled_days_after'],
  ['card_id', 'deck_instance_id', 'reviewed_at', 'session_id']
)

export const SETTINGS = spec<SettingRow>('settings', ['key', 'value', 'updated_at'], ['key'])

export const SOCRATIC_ITEMS = spec<SocraticItemRow>(
  'socratic_items',
  ['id', 'source_note_id', 'concept_id', 'concept_name', 'prompt', 'guide', 'status', 'created_at', 'updated_at'],
  ['id']
)

export const SOCRATIC_ATTEMPTS = spec<SocraticAttemptRow>('socratic_attempts', ['id', 'session_id', 'started_at', 'concept_id', 'data'], ['id'])

export const SOCRATIC_EVENTS = spec<SocraticEventRow>('socratic_events', ['id', 'attempt_id', 'kind', 'at', 'payload'], ['id'])

export const TOMBSTONES = spec<Tombstone>('tombstones', ['entity', 'key', 'deleted_at'], ['entity', 'key'])

/** Tombstone keys, built the same way as the table keys above. */
export const tombstoneKey = {
  deckInstance: (id: string) => id,
  deckFile: (driveFileId: string) => driveFileId,
  tag: (id: string) => id,
  deckTag: (deckInstanceId: string, tagId: string) => `${deckInstanceId}|${tagId}`,
  reviewState: (cardId: string, deckInstanceId: string) => `${cardId}|${deckInstanceId}`,
  session: (id: string) => id,
  socraticItem: (id: string) => id,
}

/** Stable serialization of a row (fixed column order), for equality checks. */
export function rowSignature<Row>(table: TableSpec<Row>, row: Row): string {
  return JSON.stringify(table.columns.map((c) => row[c] ?? null))
}

export function emptyReplicatedState(): ReplicatedState {
  return {
    settings: [], deckInstances: [], tags: [], deckTags: [], sessions: [], sessionDecks: [], reviewStates: [], reviewLogs: [],
    socraticItems: [], socraticAttempts: [], socraticEvents: [], tombstones: [],
  }
}
